import { serverTime } from '../../../core/time/business-time';
import { Injectable } from '@angular/core';

import { AppState, Step4CompletionState } from '../domain/registration-model';
import { RegistrationReceiptStorage } from '../integrations/registration-receipt-storage';
export * from '../domain/registration-model';

@Injectable({ providedIn: 'root' })
export class MemberStateService {
  private readonly receipts = new RegistrationReceiptStorage();
  private readonly completionStorageKey = 'smft.member.step4Completion';
  private state: AppState = this.createInitialState();
  private flow = crypto.randomUUID();
  private entryTarget = '';
  get flowId(): string { return this.flow; }
  enterTarget(target: string, credentialChanged = false): void {
    if (credentialChanged || this.entryTarget && this.entryTarget !== target) this.reset();
    this.entryTarget = target;
  }
  completedRegistration: { type: string; studioId: number | undefined } | null = null;

  private createInitialState(): AppState {
    return {
      member: { tmp: {}, payment: {}, cc: {} },
      visitors: false,
      inStudio: false,
      showConfirm: false,
      showResult: false,
      showProcess: false,
      showSuccess: false,
      showFailed: false,
      gmoCreateError: false,
      emailSent: false,
      btResult: false,
      uploadImg: {},
      lang: 'ja',
    };
  }

  get(): AppState {
    return this.state;
  }

  reset(): void {
    try {
      const receipt = this.receipts.read();
      if (receipt?.status === 'pending' || receipt?.status === 'unknown') return;
    } catch { return; }
    this.state = this.createInitialState();
    this.flow = crypto.randomUUID();
    this.entryTarget = '';
    this.completedRegistration = null;
    try { sessionStorage.removeItem(this.completionStorageKey); } catch { /* Best effort. */ }
  }

  clearReservations(): number[] {
    let storedIds: number[] = [];
    try {
      const stored = JSON.parse(localStorage.getItem('reservationId') ?? '[]');
      if (Array.isArray(stored)) storedIds = stored;
    } catch { /* Keep clearing in-memory state when storage is unavailable. */ }
    const ids = [...new Set([...(this.state.member.reservationId ?? []), ...storedIds])];
    this.state.member.reservationId = [];
    this.state.member.reservation = [];
    this.state.member.tmp.visitDatetime = undefined;
    try { localStorage.setItem('reservationId', '[]'); } catch { /* Best effort. */ }
    return ids;
  }

  setLang(lang: string): void {
    this.state.lang = lang;
    this.state.member.translate = lang;
  }

  setVisitors(v: boolean): void {
    this.state.visitors = v;
  }

  setInStudio(v: boolean): void {
    this.state.inStudio = v;
  }

  setShowConfirm(v: boolean): void { this.state.showConfirm = v; }
  setShowResult(v: boolean): void { this.state.showResult = v; }
  setShowProcess(v: boolean): void { this.state.showProcess = v; }
  setShowSuccess(v: boolean): void { this.state.showSuccess = v; }
  setShowFailed(v: boolean): void { this.state.showFailed = v; }
  setGmoCreateError(v: boolean): void { this.state.gmoCreateError = v; }
  setEmailSent(v: boolean): void { this.state.emailSent = v; }
  setBtResult(v: boolean): void { this.state.btResult = v; }

  saveStep4Completion(type: string, studioId: string | number | undefined, status: 'success' | 'failed'): void {
    if (!type || studioId === undefined || studioId === null) return;
    const completion: Step4CompletionState = {
      type,
      studioId: String(studioId),
      status,
      gmoCreateError: this.state.gmoCreateError,
      emailSent: this.state.emailSent,
      lang: this.state.member.translate ?? this.state.lang,
      memberId: this.state.member.id,
      paymentMethod: this.state.member.payment?.paymentMethod,
      btPaymentEnabled: Number(this.state.member.btPaymentEnabled ?? 0),
      savedAt: serverTime.now(), clockBasis: 'server-jst-v1',
    };
    try {
      this.receipts.write(completion);
    } catch {
      // Completion restore is best-effort only.
    }
  }

  restoreStep4Completion(type: string | undefined, studioId: string | undefined): boolean {
    if (!type || !studioId) return false;
    let completion: Step4CompletionState | null = null;
    try {
      completion = this.receipts.read();
    } catch {
      return false;
    }
    if (!completion || typeof completion !== 'object' || Array.isArray(completion)) return false;
    if (!['new_membership', 'temporary_registration', 'main_registration'].includes(completion.type)) return false;
    if (!['success', 'failed', 'unknown', 'pending'].includes(completion.status)) return false;
    if (typeof completion.savedAt !== 'number' || !Number.isFinite(completion.savedAt)) return false;
    if (completion.type !== type || completion.studioId !== String(studioId)) return false;

    this.state.showConfirm = false;
    this.state.showResult = true;
    this.state.showProcess = false;
    this.state.showSuccess = completion.status === 'success';
    this.state.showFailed = completion.status === 'failed';
    this.state.gmoCreateError = completion.gmoCreateError;
    this.state.emailSent = completion.emailSent;
    this.state.member.studioId = Number(studioId);
    this.state.member.id = completion.memberId;
    this.state.member.payment.paymentMethod = completion.paymentMethod;
    this.state.member.btPaymentEnabled = completion.btPaymentEnabled ?? 0;
    // Reloading the result page must preserve the same reentry guard as submitting.
    this.completedRegistration = completion.status === 'success'
      ? { type, studioId: Number(studioId) }
      : null;
    this.setLang(completion.lang || 'ja');
    return true;
  }
}
